import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { Star, Quote, PenLine, X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal } from "@/components/Reveal";
import { SectionHead } from "@/components/SectionHead";
import { useVisible } from "@/context/SettingsContext";
import AiWriteButton from "@/components/AiWriteButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Stars = ({ value, onChange, size = "h-4 w-4", testid = "stars" }) => (
  <div className="flex gap-1" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        disabled={!onChange}
        onClick={() => onChange && onChange(n)}
        data-testid={`${testid}-${n}`}
        className={onChange ? "transition-transform hover:scale-125" : "cursor-default"}
        aria-label={`${n} / 5`}
      >
        <Star className={`${size} ${n <= value ? "fill-gold text-gold" : "text-brand/25"}`} />
      </button>
    ))}
  </div>
);

const ReviewForm = ({ onDone, presetName = "" }) => {
  const { t } = useLanguage();
  const f = t.testimonials.form;
  const [form, setForm] = useState({ name: presetName, vehicle: "", rating: 5, comment: "" });
  const [sending, setSending] = useState(false);
  const inputClass =
    "w-full rounded-xl border border-brand/20 bg-white px-4 py-3 text-sm text-forest placeholder:text-brand/40 outline-none transition-all focus:border-gold focus:ring-4 focus:ring-gold/10";

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/reviews`, form);
      toast.success(f.success);
      onDone();
    } catch {
      toast.error(f.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-4 rounded-3xl border border-gold/20 bg-cream p-6 sm:grid-cols-2 sm:p-8" data-testid="review-form">
      <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={f.name} data-testid="review-form-name-input" className={inputClass} />
      <input value={form.vehicle} onChange={(e) => setForm({ ...form, vehicle: e.target.value })} placeholder={f.vehicle} data-testid="review-form-vehicle-input" className={inputClass} />
      <div className="flex items-center gap-4 sm:col-span-2">
        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand">{f.rating}</span>
        <Stars value={form.rating} onChange={(n) => setForm({ ...form, rating: n })} size="h-6 w-6" testid="review-form-stars" />
      </div>
      <div className="sm:col-span-2">
        <textarea required rows={4} value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} placeholder={f.commentPh} data-testid="review-form-comment-input" className={`${inputClass} resize-none`} />
        <AiWriteButton kind="review" value={form.comment} onText={(comment) => setForm((p) => ({ ...p, comment }))} context={{ vehicule: form.vehicle, note: `${form.rating}/5` }} testid="review-ai-write" />
      </div>
      <button
        type="submit"
        disabled={sending}
        data-testid="review-form-submit-button"
        className="rounded-full bg-gold px-6 py-3.5 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-white shadow-lg shadow-gold/25 transition-all hover:-translate-y-0.5 hover:bg-gold-hover disabled:opacity-60 sm:w-fit"
      >
        {sending ? "..." : f.submit}
      </button>
    </form>
  );
};

const Testimonials = () => {
  const { t } = useLanguage();
  const [reviews, setReviews] = useState([]);
  const params = new URLSearchParams(window.location.search);
  const fromInvite = params.get("avis") === "1";
  const [showForm, setShowForm] = useState(fromInvite);
  const canReview = useVisible("reviews_form");

  useEffect(() => {
    axios.get(`${API}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
    if (fromInvite) setTimeout(() => document.querySelector('[data-testid="testimonials-section"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 600);
  }, [fromInvite]);

  const avg = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : null;

  return (
    <section className="bg-mint py-24 sm:py-32" data-testid="testimonials-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
          <SectionHead overline={t.testimonials.overline} title={t.testimonials.title} accent={t.testimonials.titleAccent} sub={t.testimonials.sub} />
          <Reveal delay={0.2}>
            <div className="flex flex-wrap items-center gap-4">
              {avg && (
                <div className="flex items-center gap-3 rounded-full bg-white px-5 py-3 shadow-sm" data-testid="testimonials-average">
                  <span className="font-display text-2xl font-extrabold text-gold">{avg}</span>
                  <Stars value={Math.round(avg)} testid="testimonials-average-stars" />
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand">{reviews.length} avis</span>
                </div>
              )}
              {canReview && (
              <button
                onClick={() => setShowForm(!showForm)}
                data-testid="leave-review-button"
                className="inline-flex items-center gap-2 rounded-full border-2 border-brand bg-white px-5 py-3 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-brand transition-all hover:bg-brand hover:text-white"
              >
                {showForm ? <X className="h-4 w-4" /> : <PenLine className="h-4 w-4" />}
                {t.testimonials.leave}
              </button>
              )}
            </div>
          </Reveal>
        </div>

        <AnimatePresence>
          {showForm && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
              <div className="mt-10">
                <ReviewForm onDone={() => setShowForm(false)} presetName={params.get("nom") || ""} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {reviews.length === 0 ? (
          <Reveal>
            <p className="mt-14 rounded-3xl border-2 border-dashed border-brand/20 px-6 py-12 text-center text-forest" data-testid="testimonials-empty">
              {t.testimonials.empty}
            </p>
          </Reveal>
        ) : (
          <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {reviews.slice(0, 6).map((r, i) => (
              <Reveal key={r.id} delay={i * 0.07}>
                <article className="flex h-full flex-col rounded-3xl bg-white p-8 shadow-sm transition-all duration-500 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-brand/10" data-testid={`review-card-${i}`}>
                  <Quote className="h-8 w-8 text-gold/40" />
                  <p className="mt-4 flex-1 text-sm leading-relaxed text-forest sm:text-base">“{r.comment}”</p>
                  <div className="mt-6 flex items-center justify-between border-t border-brand/10 pt-5">
                    <div>
                      <p className="font-display text-base font-bold text-forest">{r.name}</p>
                      {r.vehicle && <p className="text-xs text-brand">{r.vehicle}</p>}
                    </div>
                    <Stars value={r.rating} testid={`review-card-${i}-stars`} />
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Testimonials;
